import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  TextField,
  MenuItem,
  Chip,
  IconButton,
  useTheme,
  alpha,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Collapse,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Checkbox,
  Paper,
  CircularProgress,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  FormLabel,
} from '@mui/material';
import {
  Add,
  SwapHoriz,
  Delete,
  Close,
  ExpandLess,
  PlayArrow,
  SaveAlt,
  PowerSettingsNew,
} from '@mui/icons-material';
import Plot from 'react-plotly.js';
import { useParams } from 'react-router-dom';
import { useProject } from '../contexts/ProjectContext';
import { useTransfer } from '../contexts/TransferContext';
import { useData } from '../contexts/DataContext';
import MainLayout from '../components/Layout/MainLayout';

const parsePlotJson = (plotData) => {
  if (!plotData) return null;
  try {
    return typeof plotData === 'string' ? JSON.parse(plotData) : plotData;
  } catch {
    return null;
  }
};

const METHOD_LABELS = {
  ann: 'ANN (CNN + BiLSTM)',
  bias: 'Bias Correction',
  pds: 'Piecewise Direct Standardization',
  ds: 'Direct Standardization',
  linear: 'Linear (Ridge Regression)',
};

const METHOD_DESCRIPTIONS = {
  ann: 'Deep learning model that auto-adapts to different spectral lengths. Best for complex non-linear sensor differences.',
  bias: 'Simple additive correction using mean difference. Fast, works when sensors are very similar.',
  pds: 'Local window-based regression per wavelength. Good balance of complexity and performance.',
  ds: 'Global linear transformation matrix. Works well for moderate sensor differences.',
  linear: 'Multivariate ridge regression with automatic regularization.',
};

const CalibrationTransfer = () => {
  const theme = useTheme();
  const { projectId } = useParams();
  const { sensors, fetchSensors } = useProject();
  const {
    transfers,
    fetchTransfers,
    trainTransfer,
    fetchTransferDetail,
    applyTransfer,
    deleteTransfer,
    toggleTransferActive,
  } = useTransfer();
  const { samples, fetchSamples } = useData();

  const [showTraining, setShowTraining] = useState(false);
  const [sourceSensor, setSourceSensor] = useState('');
  const [targetSensor, setTargetSensor] = useState('');
  const [method, setMethod] = useState('ann');
  const [training, setTraining] = useState(false);

  // Detail dialog
  const [detailTransfer, setDetailTransfer] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  // Apply section
  const [applyMode, setApplyMode] = useState(false);
  const [selectedSamples, setSelectedSamples] = useState([]);
  const [applying, setApplying] = useState(false);
  const [transferResults, setTransferResults] = useState(null);

  useEffect(() => {
    if (projectId) {
      fetchSensors(projectId);
      fetchTransfers(projectId);
      fetchSamples(projectId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  // Filter target sensors to exclude source
  const targetSensors = useMemo(
    () => sensors.filter((s) => s.id !== sourceSensor),
    [sensors, sourceSensor]
  );

  const handleTrain = async () => {
    if (!sourceSensor || !targetSensor || !method) return;
    setTraining(true);
    try {
      await trainTransfer(projectId, sourceSensor, targetSensor, method);
      await fetchTransfers(projectId);
    } catch (error) {
      console.error('Transfer training failed:', error);
    } finally {
      setTraining(false);
    }
  };

  const handleDelete = async (transferId) => {
    if (window.confirm('Delete this calibration transfer model?')) {
      await deleteTransfer(transferId);
      await fetchTransfers(projectId);
      if (detailTransfer?.id === transferId) {
        setDetailTransfer(null);
      }
    }
  };

  const handleOpenDetail = async (transfer) => {
    setLoadingDetail(true);
    setApplyMode(false);
    setTransferResults(null);
    setSelectedSamples([]);
    try {
      const detail = await fetchTransferDetail(transfer.id);
      setDetailTransfer(detail);
    } catch (error) {
      console.error('Failed to load transfer details:', error);
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleApply = async (saveAsNew = false) => {
    if (!detailTransfer || selectedSamples.length === 0) return;
    setApplying(true);
    try {
      const result = await applyTransfer(detailTransfer.id, selectedSamples, saveAsNew);
      setTransferResults(result);
      if (saveAsNew && projectId) {
        fetchSamples(projectId);
      }
    } catch (error) {
      console.error('Apply transfer failed:', error);
    } finally {
      setApplying(false);
    }
  };

  const handleToggleActive = async () => {
    if (!detailTransfer) return;
    try {
      const updated = await toggleTransferActive(detailTransfer.id);
      setDetailTransfer((prev) => ({ ...prev, is_active: updated.is_active }));
      await fetchTransfers(projectId);
    } catch (error) {
      console.error('Toggle active failed:', error);
    }
  };

  const toggleSampleSelection = (sampleId) => {
    setSelectedSamples((prev) =>
      prev.includes(sampleId) ? prev.filter((id) => id !== sampleId) : [...prev, sampleId]
    );
  };

  const selectAllSamples = (sampleList) => {
    const allIds = sampleList.map((s) => s.id);
    const allSelected = allIds.every((id) => selectedSamples.includes(id));
    if (allSelected) {
      setSelectedSamples((prev) => prev.filter((id) => !allIds.includes(id)));
    } else {
      setSelectedSamples((prev) => [...new Set([...prev, ...allIds])]);
    }
  };

  // Samples from the source sensor available for transfer
  const availableSamples = useMemo(() => {
    if (!detailTransfer) return [];
    const srcSensorId = detailTransfer.source_sensor;
    return samples.filter(
      (s) => s.sensor === srcSensorId && s.spectrum && s.spectrum.length > 0
    );
  }, [samples, detailTransfer]);

  const parsedEvalPlot = useMemo(
    () => parsePlotJson(detailTransfer?.eval_plotly_json),
    [detailTransfer?.eval_plotly_json]
  );

  const plotLayout = (parsed) => ({
    ...parsed?.layout,
    autosize: true,
    width: undefined,
    height: 500,
    title: { text: '' },
    plot_bgcolor: 'rgba(0,0,0,0)',
    paper_bgcolor: 'rgba(0,0,0,0)',
    margin: { l: 80, r: 40, t: 40, b: 80 },
  });

  return (
    <MainLayout>
      <Container maxWidth="lg">
        <Box sx={{ mb: 4 }}>
          <Typography variant="h3" fontWeight={700} gutterBottom>
            Calibration Transfer
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Transfer spectra between sensors using classical or deep learning methods
          </Typography>
        </Box>

        {/* Train Toggle */}
        <Card sx={{ mb: 4, borderRadius: 3 }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6" fontWeight={600}>
                Calibration Transfer Models ({transfers.length})
              </Typography>
              <Button
                variant={showTraining ? 'outlined' : 'contained'}
                startIcon={showTraining ? <ExpandLess /> : <Add />}
                onClick={() => setShowTraining(!showTraining)}
                disabled={sensors.length < 2}
              >
                {showTraining ? 'Hide Training' : 'Train New Transfer'}
              </Button>
            </Box>
            {sensors.length < 2 && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                At least 2 sensors are required for calibration transfer.
              </Typography>
            )}
          </CardContent>
        </Card>

        {/* Train Panel */}
        <Collapse in={showTraining && sensors.length >= 2}>
          <Card sx={{ mb: 4, borderRadius: 3 }}>
            <CardContent sx={{ p: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <SwapHoriz sx={{ fontSize: 32, color: theme.palette.primary.main }} />
                <Box>
                  <Typography variant="h5" fontWeight={600}>
                    Train Calibration Transfer
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Paired samples (same name on both sensors) will be auto-matched
                  </Typography>
                </Box>
              </Box>

              <Grid container spacing={3}>
                <Grid item xs={12} md={5}>
                  <TextField
                    select
                    fullWidth
                    label="Source Sensor (Slave)"
                    value={sourceSensor}
                    onChange={(e) => {
                      setSourceSensor(e.target.value);
                      if (e.target.value === targetSensor) setTargetSensor('');
                    }}
                  >
                    {sensors.map((s) => (
                      <MenuItem key={s.id} value={s.id}>
                        {s.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={2} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <SwapHoriz sx={{ fontSize: 28, color: 'text.secondary' }} />
                </Grid>
                <Grid item xs={12} md={5}>
                  <TextField
                    select
                    fullWidth
                    label="Target Sensor (Master)"
                    value={targetSensor}
                    onChange={(e) => setTargetSensor(e.target.value)}
                    disabled={!sourceSensor}
                  >
                    {targetSensors.map((s) => (
                      <MenuItem key={s.id} value={s.id}>
                        {s.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={12}>
                  <FormControl component="fieldset">
                    <FormLabel component="legend" sx={{ mb: 1, fontWeight: 600 }}>
                      Transfer Method
                    </FormLabel>
                    <RadioGroup
                      value={method}
                      onChange={(e) => setMethod(e.target.value)}
                    >
                      {Object.entries(METHOD_LABELS).map(([key, label]) => (
                        <FormControlLabel
                          key={key}
                          value={key}
                          control={<Radio />}
                          label={
                            <Box>
                              <Typography variant="body1" fontWeight={500}>
                                {label}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {METHOD_DESCRIPTIONS[key]}
                              </Typography>
                            </Box>
                          }
                          sx={{ mb: 1, alignItems: 'flex-start', '& .MuiRadio-root': { mt: 0.5 } }}
                        />
                      ))}
                    </RadioGroup>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <Button
                    fullWidth
                    variant="contained"
                    size="large"
                    startIcon={
                      training ? <CircularProgress size={20} color="inherit" /> : <Add />
                    }
                    onClick={handleTrain}
                    disabled={!sourceSensor || !targetSensor || training}
                    sx={{ py: 1.8 }}
                  >
                    {training ? 'Training Transfer Model...' : 'Train Transfer Model'}
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Collapse>

        {/* Transfer Models List */}
        {transfers.length === 0 ? (
          <Card sx={{ borderRadius: 3 }}>
            <CardContent sx={{ p: 6, textAlign: 'center' }}>
              <SwapHoriz sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No Transfer Models Yet
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Train a calibration transfer model to map spectra between sensors
              </Typography>
            </CardContent>
          </Card>
        ) : (
          <Grid container spacing={3}>
            {transfers.map((t) => (
              <Grid item xs={12} sm={6} md={4} key={t.id}>
                <Card
                  onClick={() => handleOpenDetail(t)}
                  sx={{
                    borderRadius: 3,
                    cursor: 'pointer',
                    transition: 'all 0.3s',
                    border: t.is_active
                      ? `2px solid ${theme.palette.success.main}`
                      : '2px solid transparent',
                    opacity: t.is_active === false ? 0.7 : 1,
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: theme.shadows[8],
                    },
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        <Chip
                          label={METHOD_LABELS[t.method] || t.method}
                          color="primary"
                          size="small"
                        />
                        <Chip label={`v${t.version || 1}`} size="small" variant="outlined" />
                        <Chip
                          label={t.is_active ? 'Active' : 'Inactive'}
                          size="small"
                          color={t.is_active ? 'success' : 'default'}
                          variant={t.is_active ? 'filled' : 'outlined'}
                        />
                      </Box>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(t.id);
                        }}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Box>

                    <Typography variant="body1" fontWeight={600} gutterBottom>
                      {t.source_sensor_name} &rarr; {t.target_sensor_name}
                    </Typography>

                    <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Pairs:</strong> {t.n_transfer_samples} ({t.n_train_samples} / {t.n_test_samples})
                      </Typography>
                      {t.scores?.r2 !== undefined && (
                        <Typography variant="body2" color="text.secondary">
                          <strong>R&sup2;:</strong> {t.scores.r2.toFixed(4)}
                        </Typography>
                      )}
                      {t.scores?.rmse !== undefined && (
                        <Typography variant="body2" color="text.secondary">
                          <strong>RMSE:</strong> {t.scores.rmse.toFixed(4)}
                        </Typography>
                      )}
                      {t.created_at && (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                          {new Date(t.created_at).toLocaleDateString()}
                        </Typography>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        {/* Transfer Detail Dialog */}
        <Dialog
          open={!!detailTransfer}
          onClose={() => setDetailTransfer(null)}
          maxWidth="md"
          fullWidth
          PaperProps={{ sx: { maxHeight: '90vh' } }}
        >
          {detailTransfer && (
            <>
              <DialogTitle
                sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
              >
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Typography variant="h5" fontWeight={600}>
                      {detailTransfer.source_sensor_name} &rarr; {detailTransfer.target_sensor_name}
                    </Typography>
                    <Chip label={`v${detailTransfer.version || 1}`} size="small" variant="outlined" />
                    <Chip
                      label={detailTransfer.is_active ? 'Active' : 'Inactive'}
                      size="small"
                      color={detailTransfer.is_active ? 'success' : 'default'}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                    <Chip
                      label={METHOD_LABELS[detailTransfer.method] || detailTransfer.method}
                      color="primary"
                      size="small"
                    />
                    <Chip
                      label={`${detailTransfer.n_transfer_samples} pairs`}
                      variant="outlined"
                      size="small"
                    />
                    <Chip
                      label={`${detailTransfer.n_train_samples} train / ${detailTransfer.n_test_samples} test`}
                      variant="outlined"
                      size="small"
                    />
                  </Box>
                </Box>
                <IconButton onClick={() => setDetailTransfer(null)}>
                  <Close />
                </IconButton>
              </DialogTitle>

              <DialogContent dividers>
                {/* Scores */}
                {detailTransfer.scores && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" fontWeight={600} gutterBottom>
                      Transfer Quality Metrics
                    </Typography>
                    <Grid container spacing={2}>
                      {Object.entries(detailTransfer.scores).map(([key, value]) => (
                        <Grid item xs={6} sm={4} md={2.4} key={key}>
                          <Box
                            sx={{
                              p: 2,
                              borderRadius: 2,
                              bgcolor: alpha(theme.palette.primary.main, 0.05),
                              textAlign: 'center',
                            }}
                          >
                            <Typography variant="caption" color="text.secondary">
                              {key.toUpperCase().replace('_', ' ')}
                            </Typography>
                            <Typography variant="h6" fontWeight={600}>
                              {typeof value === 'number' ? value.toFixed(4) : String(value)}
                            </Typography>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                )}

                {/* Evaluation Plot */}
                {parsedEvalPlot && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" fontWeight={600} gutterBottom>
                      Actual vs Predicted Target Spectra
                    </Typography>
                    <Plot
                      data={parsedEvalPlot.data}
                      layout={plotLayout(parsedEvalPlot)}
                      useResizeHandler
                      config={{ responsive: true, displayModeBar: false, displaylogo: false }}
                      style={{ width: '100%', height: 500 }}
                    />
                  </Box>
                )}

                {/* Hyperparameters */}
                {detailTransfer.hyperparameters && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" fontWeight={600} gutterBottom>
                      Hyperparameters
                    </Typography>
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        bgcolor: alpha(theme.palette.primary.main, 0.03),
                      }}
                    >
                      <Grid container spacing={1}>
                        {Object.entries(detailTransfer.hyperparameters).map(([key, value]) => (
                          <Grid item xs={6} sm={4} md={3} key={key}>
                            <Typography variant="caption" color="text.secondary">
                              {key}
                            </Typography>
                            <Typography variant="body2" fontWeight={500}>
                              {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                            </Typography>
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  </Box>
                )}

                {/* Apply Transfer Section */}
                <Box sx={{ mt: 2 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      mb: 2,
                    }}
                  >
                    <Typography variant="h6" fontWeight={600}>
                      Apply Transfer to Samples
                    </Typography>
                    <Button
                      variant={applyMode ? 'outlined' : 'contained'}
                      size="small"
                      startIcon={applyMode ? <ExpandLess /> : <PlayArrow />}
                      onClick={() => {
                        setApplyMode(!applyMode);
                        setTransferResults(null);
                        setSelectedSamples([]);
                      }}
                    >
                      {applyMode ? 'Hide' : 'Select Samples'}
                    </Button>
                  </Box>

                  <Collapse in={applyMode}>
                    {availableSamples.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        No samples found on the source sensor.
                      </Typography>
                    ) : (
                      <>
                        <Paper sx={{ maxHeight: 300, overflow: 'auto', mb: 2 }}>
                          <Table size="small" stickyHeader>
                            <TableHead>
                              <TableRow>
                                <TableCell padding="checkbox">
                                  <Checkbox
                                    indeterminate={
                                      selectedSamples.length > 0 &&
                                      selectedSamples.length < availableSamples.length
                                    }
                                    checked={
                                      availableSamples.length > 0 &&
                                      availableSamples.every((s) =>
                                        selectedSamples.includes(s.id)
                                      )
                                    }
                                    onChange={() => selectAllSamples(availableSamples)}
                                    size="small"
                                  />
                                </TableCell>
                                <TableCell>Name</TableCell>
                                <TableCell>Status</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {availableSamples.map((sample) => (
                                <TableRow
                                  key={sample.id}
                                  hover
                                  onClick={() => toggleSampleSelection(sample.id)}
                                  sx={{ cursor: 'pointer' }}
                                >
                                  <TableCell padding="checkbox">
                                    <Checkbox
                                      checked={selectedSamples.includes(sample.id)}
                                      size="small"
                                    />
                                  </TableCell>
                                  <TableCell>{sample.name}</TableCell>
                                  <TableCell>
                                    <Chip label={sample.status} size="small" variant="outlined" />
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </Paper>

                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                          <Button
                            variant="contained"
                            startIcon={
                              applying ? (
                                <CircularProgress size={16} color="inherit" />
                              ) : (
                                <PlayArrow />
                              )
                            }
                            onClick={() => handleApply(false)}
                            disabled={selectedSamples.length === 0 || applying}
                          >
                            {applying ? 'Transforming...' : `Preview (${selectedSamples.length})`}
                          </Button>
                          <Button
                            variant="contained"
                            color="success"
                            startIcon={
                              applying ? (
                                <CircularProgress size={16} color="inherit" />
                              ) : (
                                <SaveAlt />
                              )
                            }
                            onClick={() => handleApply(true)}
                            disabled={selectedSamples.length === 0 || applying}
                          >
                            Transform & Save ({selectedSamples.length})
                          </Button>
                          <Typography variant="body2" color="text.secondary">
                            {selectedSamples.length} of {availableSamples.length} selected
                          </Typography>
                        </Box>

                        {/* Transfer Results */}
                        {transferResults && (
                          <Paper sx={{ mt: 2, maxHeight: 300, overflow: 'auto' }}>
                            <Table size="small" stickyHeader>
                              <TableHead>
                                <TableRow>
                                  <TableCell>Sample</TableCell>
                                  <TableCell>Spectrum Length</TableCell>
                                  {transferResults.saved && <TableCell>New Sample ID</TableCell>}
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {(transferResults.results || []).map((r, i) => (
                                  <TableRow key={i}>
                                    <TableCell>{r.sample_name}</TableCell>
                                    <TableCell>
                                      {r.transferred_spectrum
                                        ? r.transferred_spectrum.length
                                        : '—'}
                                    </TableCell>
                                    {transferResults.saved && (
                                      <TableCell>
                                        {r.new_sample_id ? (
                                          <Chip
                                            label={`#${r.new_sample_id}`}
                                            color="success"
                                            size="small"
                                          />
                                        ) : (
                                          '—'
                                        )}
                                      </TableCell>
                                    )}
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </Paper>
                        )}
                      </>
                    )}
                  </Collapse>
                </Box>
              </DialogContent>

              <DialogActions sx={{ justifyContent: 'space-between' }}>
                <Button
                  variant={detailTransfer.is_active ? 'outlined' : 'contained'}
                  color={detailTransfer.is_active ? 'warning' : 'success'}
                  startIcon={<PowerSettingsNew />}
                  onClick={handleToggleActive}
                >
                  {detailTransfer.is_active ? 'Deactivate' : 'Activate'}
                </Button>
                <Button onClick={() => setDetailTransfer(null)}>Close</Button>
              </DialogActions>
            </>
          )}
        </Dialog>
      </Container>
    </MainLayout>
  );
};

export default CalibrationTransfer;
